import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Snackbar,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { API_BASE_URL } from "../../config/api";
import "../PlayerShareModal/PlayerShareModal.css";

function shareOrigin() {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
        return API_BASE_URL;
    }
    return window.location.origin;
}

/**
 * Generic share modal for server-rendered chart PNGs (image-gen architecture).
 *
 * Props:
 * - cardPath: API path under /share/.../card (e.g. /share/draft-debuts/by-years/card)
 * - sharePath: OG/share page path (e.g. /share/draft-debuts/by-years)
 * - title / description / fileName
 * - layoutOptions / layout / onLayoutChange: optional Breakdown vs Players toggle
 */
function ChartShareModal({
    open,
    onClose,
    cardPath,
    sharePath,
    title = "Share chart",
    description = "Download or share this chart image.",
    fileName = "chart-share.png",
    layoutOptions = null,
    layout = null,
    onLayoutChange = null,
}) {
    const previewBlobRef = useRef(null);
    const [previewSrc, setPreviewSrc] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [downloading, setDownloading] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(true);
    const [sharing, setSharing] = useState(false);

    const cardUrl = useMemo(() => {
        if (!cardPath) return "";
        return `${shareOrigin()}${cardPath.startsWith("/") ? cardPath : `/${cardPath}`}`;
    }, [cardPath]);

    const shareUrl = useMemo(() => {
        if (!sharePath) return "";
        return `${shareOrigin()}${sharePath.startsWith("/") ? sharePath : `/${sharePath}`}`;
    }, [sharePath]);

    const previewReady = Boolean(previewSrc) && !previewLoading;
    const showLayoutToggle = Array.isArray(layoutOptions) && layoutOptions.length > 1;

    useEffect(() => {
        if (!open || !cardUrl) return undefined;

        const abortController = new AbortController();
        let cancelled = false;

        setPreviewLoading(true);
        previewBlobRef.current = null;
        setPreviewSrc((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return "";
        });

        (async () => {
            try {
                const res = await fetch(cardUrl, { signal: abortController.signal });
                if (!res.ok) throw new Error("Failed to fetch image");
                const blob = await res.blob();
                if (cancelled) return;
                previewBlobRef.current = blob;
                setPreviewSrc(URL.createObjectURL(blob));
                setPreviewLoading(false);
            } catch (err) {
                if (!cancelled && err?.name !== "AbortError") {
                    setPreviewLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
            abortController.abort();
        };
    }, [open, cardUrl]);

    useEffect(() => {
        if (open) return undefined;
        setPreviewSrc((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return "";
        });
        previewBlobRef.current = null;
        return undefined;
    }, [open]);

    const showSnackbar = (message) => {
        setSnackbarMessage(message);
        setSnackbarOpen(true);
    };

    const handleDownload = async () => {
        if (!previewBlobRef.current) return;
        setDownloading(true);
        try {
            const url = URL.createObjectURL(previewBlobRef.current);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = fileName;
            anchor.click();
            URL.revokeObjectURL(url);
        } finally {
            setDownloading(false);
        }
    };

    const handleCopyLink = async () => {
        if (!shareUrl) return;
        try {
            await navigator.clipboard.writeText(shareUrl);
            showSnackbar("Link copied");
        } catch {
            showSnackbar("Unable to copy link");
        }
    };

    const handleNativeShare = async () => {
        if (!previewBlobRef.current) return;
        setSharing(true);
        try {
            const file = new File([previewBlobRef.current], fileName, {
                type: "image/png",
            });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    title,
                    text: description,
                    url: shareUrl || undefined,
                    files: [file],
                });
            } else if (navigator.share) {
                await navigator.share({
                    title,
                    text: description,
                    url: shareUrl || undefined,
                });
            } else {
                await handleCopyLink();
            }
        } catch (err) {
            if (err?.name !== "AbortError") {
                showSnackbar("Unable to share");
            }
        } finally {
            setSharing(false);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ className: "player-share-dialog-paper" }}
            >
                <DialogTitle className="player-share-dialog-title">{title}</DialogTitle>
                <DialogContent className="player-share-dialog-content">
                    <Typography component="p" className="player-share-dialog-lead">
                        {description}
                    </Typography>
                    {showLayoutToggle ? (
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={layout}
                            onChange={(_event, next) => {
                                if (next && onLayoutChange) onLayoutChange(next);
                            }}
                            aria-label="Share card layout"
                            sx={{ mb: 1.5 }}
                        >
                            {layoutOptions.map((option) => (
                                <ToggleButton key={option.id} value={option.id}>
                                    {option.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    ) : null}
                    <div className="player-share-preview-frame">
                        {previewLoading && (
                            <div className="player-share-preview-loading">
                                <CircularProgress size={28} sx={{ color: "var(--brand-strong)" }} />
                                <Typography className="player-share-preview-loading-text">
                                    Generating image…
                                </Typography>
                            </div>
                        )}
                        {previewSrc ? (
                            <img
                                src={previewSrc}
                                alt={title}
                                className={`player-share-preview-img${previewLoading ? " player-share-preview-img--loading" : ""}`}
                            />
                        ) : null}
                        {!previewLoading && !previewSrc ? (
                            <div className="player-share-preview-loading">
                                <Typography className="player-share-preview-loading-text">
                                    Unable to generate image.
                                </Typography>
                            </div>
                        ) : null}
                    </div>
                </DialogContent>
                <DialogActions className="player-share-dialog-actions">
                    <Button
                        onClick={onClose}
                        size="small"
                        className="player-share-btn-secondary"
                    >
                        Close
                    </Button>
                    <Box className="player-share-dialog-actions-group">
                        <Button
                            onClick={handleDownload}
                            disabled={downloading || !previewReady}
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadRoundedIcon />}
                            className="player-share-btn-secondary"
                        >
                            Download
                        </Button>
                        <Button
                            onClick={handleCopyLink}
                            size="small"
                            variant="outlined"
                            startIcon={<LinkRoundedIcon />}
                            className="player-share-btn-secondary"
                        >
                            Copy link
                        </Button>
                        <Button
                            onClick={handleNativeShare}
                            disabled={sharing || !previewReady}
                            size="small"
                            variant="contained"
                            disableElevation
                            startIcon={<ShareOutlinedIcon />}
                            className="player-share-btn-primary"
                        >
                            {sharing ? "Sharing…" : "Share"}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity="success"
                    variant="filled"
                    sx={{
                        width: "100%",
                        backgroundColor: "var(--bg-elevated)",
                        color: "var(--text-primary)",
                        border: "1px solid var(--border-default)",
                        "& .MuiAlert-icon": { color: "var(--brand-strong)" },
                    }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

export default ChartShareModal;
